import { EventDto, MarketDto, RecursivePartial, SelectionDto } from '../api/sbapi/sbapi-api-model';
import {
	isMessageEventAdded,
	isMessageEventDelta,
	isMessageEventRemoved,
	isMessageInitialState,
	isMessageMarketAdded,
	isMessageMarketDelta,
	isMessageMarketRemoved,
	isMessageSelectionAdded,
	isMessageSelectionDelta,
	isMessageSelectionRemoved,
	StreamMessage
} from '../api/sportsbook-api-types';
import reportSportsbookError from './sportsbook-error-reporter';

/** Immutably adds newValues to element, writing over existing values */
const mergeElement = <Element, Value>(array: Element[], index: number, newValues: Value) => {
	return Object.assign([], array, {
		[index]: {
			...array[index],
			...newValues
		}
	});
};

// Deep merge utility to update objects only with new data. DOESN'T clone arrays and objects when replacing them in target.
export const deepMerge = <T>(target: T, source: RecursivePartial<T>): T => {
	if (!source || typeof source !== 'object') return source as T;

	return Object.keys(source).reduce(
		(acc, key) => {
			const srcValue = source[key as keyof T];
			const tgtValue = target?.[key as keyof T]; // Target value might be undefined, for example when adding a new property.

			acc[key as keyof T] =
				tgtValue && srcValue && typeof srcValue === 'object' && !Array.isArray(srcValue)
					? deepMerge(tgtValue, srcValue)
					: (srcValue as T[keyof T]); // Preserve the source reference here.

			return acc;
		},
		{ ...target }
	);
};

type NormalizedData = {
	events: EventDto[];
	markets: MarketDto[];
	selections: SelectionDto[];
};

/**
 * Immutably merges the normalized data with the stream messages.
 * @param normalizedData - The normalized data to merge with the stream messages.
 * @param messages - The stream messages to merge with the normalized data.
 * @returns The merged normalized data.
 */
const mergeNormalizedDataWithStreamMessages = (
	{ events, markets, selections }: NormalizedData,
	messages: StreamMessage[]
): NormalizedData => {
	return messages.reduce(
		(acc, m) => {
			switch (true) {
				// INITIAL STATE

				case isMessageInitialState(m):
					return {
						events: m.payload.events,
						markets: m.payload.markets,
						selections: m.payload.selections
					};

				// ADDED

				case isMessageEventAdded(m): {
					const findEventIndex = acc.events.findIndex(e => e.id === m.payload.event.id);
					return {
						...acc,
						events:
							findEventIndex !== -1
								? mergeElement(acc.events, findEventIndex, m.payload.event)
								: [...acc.events, m.payload.event]
					};
				}

				case isMessageMarketAdded(m): {
					const findMarketIndex = acc.markets.findIndex(e => e.id === m.payload.market.id);
					return {
						...acc,
						markets:
							findMarketIndex !== -1
								? mergeElement(acc.markets, findMarketIndex, m.payload.market)
								: [...acc.markets, m.payload.market]
					};
				}

				case isMessageSelectionAdded(m): {
					const findSelectionIndex = acc.selections.findIndex(e => e.id === m.payload.selection.id);
					return {
						...acc,
						selections:
							findSelectionIndex !== -1
								? mergeElement(acc.selections, findSelectionIndex, m.payload.selection)
								: [...acc.selections, m.payload.selection]
					};
				}

				// REMOVED

				case isMessageEventRemoved(m): {
					const findEventIndex = acc.events.findIndex(e => e.id === m.payload.eventId);
					return findEventIndex !== -1
						? {
								...acc,
								events: acc.events.filter(e => e.id !== m.payload.eventId)
							}
						: acc;
				}

				case isMessageMarketRemoved(m): {
					const findMarketIndex = acc.markets.findIndex(e => e.id === m.payload.marketId);
					return findMarketIndex !== -1
						? {
								...acc,
								markets: acc.markets.filter(e => e.id !== m.payload.marketId)
							}
						: acc;
				}

				case isMessageSelectionRemoved(m): {
					const findSelectionIndex = acc.selections.findIndex(e => e.id === m.payload.selectionId);
					return findSelectionIndex !== -1
						? {
								...acc,
								selections: acc.selections.filter(e => e.id !== m.payload.selectionId)
							}
						: acc;
				}

				// DELTA

				case isMessageEventDelta(m): {
					const findEventIndex = acc.events.findIndex(e => e.id === m.payload.delta.id);
					return findEventIndex !== -1
						? {
								...acc,
								events: mergeElement(
									acc.events,
									findEventIndex,
									deepMerge(acc.events[findEventIndex], m.payload.delta)
								)
							}
						: acc;
				}

				case isMessageMarketDelta(m): {
					const findMarketIndex = acc.markets.findIndex(e => e.id === m.payload.delta.id);
					return findMarketIndex !== -1
						? {
								...acc,
								markets: mergeElement(
									acc.markets,
									findMarketIndex,
									deepMerge(acc.markets[findMarketIndex], m.payload.delta)
								)
							}
						: acc;
				}

				case isMessageSelectionDelta(m): {
					const findSelectionIndex = acc.selections.findIndex(e => e.id === m.payload.delta.id);
					return findSelectionIndex !== -1
						? {
								...acc,
								selections: mergeElement(
									acc.selections,
									findSelectionIndex,
									deepMerge(acc.selections[findSelectionIndex], m.payload.delta)
								)
							}
						: acc;
				}

				// DEFAULT

				default: {
					reportSportsbookError(`Unknown stream message`, m);
					return acc;
				}
			}
		},
		{ events, markets, selections }
	);
};

export default mergeNormalizedDataWithStreamMessages;
